import { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Sheet } from "@/components/ui/sheet";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Toolbar } from "@/components/ui/toolbar";
import { FormSection, Field, ValidationSummary, FieldNote } from "@/components/ui/form-section";
import { DataTable, type Column } from "@/components/ui/data-table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { PageHeader } from "@/components/ui/page-header";
import { useExtensions, useCreateExtension, useUpdateExtension, useDeleteExtension } from "@/hooks/use-extensions";
import { useContexts } from "@/hooks/use-contexts";
import { useEndpoints } from "@/hooks/use-endpoints";
import { useWebsocketEndpoints } from "@/hooks/use-websocket-endpoints";
import { useAriApps } from "@/hooks/use-ari-apps";
import type { DialplanExtension, ActionType, ActionCreate } from "@/types";
import { CODECS } from "@/types";
import { Plus, Trash2, ChevronDown, ChevronUp, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { HelpIcon } from "@/components/ui/help-icon";

const ACTION_TYPES: { value: ActionType; label: string }[] = [
  { value: "answer", label: "Answer" },
  { value: "hangup", label: "Hangup" },
  { value: "dial", label: "Dial" },
  { value: "voicemail", label: "Voicemail" },
  { value: "set", label: "Set" },
  { value: "goto", label: "Goto" },
  { value: "stasis", label: "Stasis" },
  { value: "playback", label: "Playback" },
  { value: "noop", label: "NoOp" },
  { value: "verbose", label: "Verbose" },
  { value: "wait", label: "Wait" },
  { value: "queue", label: "Queue" },
  { value: "mixmonitor", label: "MixMonitor" },
  { value: "return", label: "Return" },
  { value: "bridge", label: "Bridge" },
  { value: "echo", label: "Echo" },
  { value: "record", label: "Record" },
  { value: "sipaddheader", label: "SIP Add Header" },
  { value: "senddtmf", label: "SendDTMF" },
];

const ACTION_LABELS: Record<string, string> = Object.fromEntries(
  ACTION_TYPES.map((t) => [t.value, t.label])
);

/** Numeric params: empty input means "unset — use this default on save". */
const NUM_DEFAULTS: Partial<Record<ActionType, Record<string, number>>> = {
  answer: { delay: 0 },
  dial: { timeout: 30 },
  goto: { priority: 1 },
  verbose: { level: 0 },
  wait: { seconds: 1 },
  record: { silence: 0, maxduration: 0 },
  senddtmf: { timeout_ms: 250, duration_ms: 0 },
};

/** ";" starts a comment and "," separates dialplan arguments. */
const ILLEGAL_EXTENSION_CHARS = /[;,\s\r\n]/;
const EXTENSION_MAX_CHARS = 80;

/** ITU-T Q.850 causes Asterisk accepts on Hangup(). */
const HANGUP_CAUSES: { value: string; label: string }[] = [
  { value: "", label: "Default (16 — normal clearing)" },
  { value: "1", label: "1 — unallocated number" },
  { value: "16", label: "16 — normal clearing" },
  { value: "17", label: "17 — user busy" },
  { value: "18", label: "18 — no user responding" },
  { value: "19", label: "19 — no answer from user" },
  { value: "21", label: "21 — call rejected" },
  { value: "22", label: "22 — number changed" },
  { value: "27", label: "27 — destination out of order" },
  { value: "28", label: "28 — invalid number format" },
  { value: "34", label: "34 — no circuit/channel available" },
  { value: "38", label: "38 — network out of order" },
  { value: "41", label: "41 — temporary failure" },
  { value: "42", label: "42 — switching equipment congestion" },
  { value: "58", label: "58 — bearer capability not available" },
  { value: "88", label: "88 — incompatible destination" },
  { value: "102", label: "102 — recovery on timer expiry" },
];

const VERBOSE_LEVELS: { value: number; label: string }[] = [
  { value: 0, label: "0 — always logged" },
  { value: 1, label: "1 — logged at verbosity 1+" },
  { value: 2, label: "2 — logged at verbosity 2+" },
  { value: 3, label: "3 — logged at verbosity 3+" },
  { value: 4, label: "4 — logged at verbosity 4+" },
];

interface FlagOption {
  flag: string;
  label: string;
  help: string;
}

const DIAL_FLAGS: FlagOption[] = [
  { flag: "t", label: "callee transfer", help: "Allow the called party to transfer the call using the blind-transfer feature code." },
  { flag: "T", label: "caller transfer", help: "Allow the calling party to transfer the call using the blind-transfer feature code." },
  { flag: "h", label: "callee hangup", help: "Allow the called party to hang up by pressing *." },
  { flag: "H", label: "caller hangup", help: "Allow the calling party to hang up by pressing *." },
  { flag: "m", label: "music on hold", help: "Play music on hold to the caller instead of ringback." },
  { flag: "r", label: "ringback", help: "Generate ringback tone to the caller locally." },
  { flag: "g", label: "continue on hangup", help: "Continue with the next priority when the called party hangs up." },
  { flag: "e", label: "run h extension", help: "Execute the h extension of the called channel's context on hangup." },
  { flag: "o", label: "original caller ID", help: "Preserve the original caller ID instead of replacing it." },
  { flag: "f", label: "force caller ID", help: "Force the caller ID of the outbound leg to the dialling extension." },
  { flag: "k", label: "callee park", help: "Allow the called party to park the call with the parking feature code." },
  { flag: "K", label: "caller park", help: "Allow the calling party to park the call with the parking feature code." },
];

const QUEUE_OPTIONS: FlagOption[] = [
  { flag: "t", label: "callee transfer", help: "Allow the queue member to transfer the caller." },
  { flag: "T", label: "caller transfer", help: "Allow the caller to transfer the call." },
  { flag: "h", label: "callee hangup", help: "Allow the queue member to hang up by pressing *." },
  { flag: "H", label: "caller hangup", help: "Allow the caller to hang up by pressing *." },
  { flag: "n", label: "no retries", help: "Do not retry members after the timeout; leave the queue instead." },
  { flag: "r", label: "ringback", help: "Ring instead of playing music on hold while waiting." },
  { flag: "c", label: "continue on hangup", help: "Continue in the dialplan even if the caller hangs up while queued." },
  { flag: "i", label: "ignore forwards", help: "Ignore call-forward requests from queue members." },
  { flag: "I", label: "ignore CONNECTEDLINE", help: "Ignore connected-line and redirecting updates from members." },
  { flag: "x", label: "callee record", help: "Allow the queue member to start one-touch recording." },
  { flag: "X", label: "caller record", help: "Allow the caller to start one-touch recording." },
];

const MIXMONITOR_OPTIONS: FlagOption[] = [
  { flag: "a", label: "append", help: "Append to the file instead of overwriting it." },
  { flag: "b", label: "bridged only", help: "Only record audio while the channel is bridged to another channel." },
];

const BRIDGE_OPTIONS: FlagOption[] = [
  { flag: "p", label: "courtesy tone", help: "Play a courtesy tone to the channel being bridged in." },
  { flag: "h", label: "callee hangup", help: "Allow the called party to hang up by pressing *." },
  { flag: "H", label: "caller hangup", help: "Allow the calling party to hang up by pressing *." },
  { flag: "t", label: "callee transfer", help: "Allow the called party to blind-transfer the call." },
  { flag: "T", label: "caller transfer", help: "Allow the calling party to blind-transfer the call." },
  { flag: "k", label: "callee park", help: "Allow the called party to park the call." },
  { flag: "K", label: "caller park", help: "Allow the calling party to park the call." },
];

const RECORD_OPTIONS: FlagOption[] = [
  { flag: "a", label: "append", help: "Append to an existing recording instead of overwriting it." },
  { flag: "n", label: "no answer", help: "Do not answer the channel first, but record anyway." },
  { flag: "q", label: "quiet", help: "Do not play the beep before recording starts." },
  { flag: "s", label: "skip if unanswered", help: "Skip recording entirely if the channel has not been answered." },
  { flag: "t", label: "* terminates", help: "Use * as the terminator key instead of #." },
  { flag: "x", label: "no DTMF terminator", help: "Ignore all terminator keys and record until hangup." },
  { flag: "y", label: "any DTMF terminates", help: "Stop the recording as soon as any DTMF digit is received." },
];

const SENDDTMF_OPTIONS: FlagOption[] = [
  { flag: "a", label: "answer first", help: "Answer the target channel if it is not already up." },
];

const VOICEMAIL_FLAGS: FlagOption[] = [
  { flag: "u", label: "unavailable greeting", help: "Play the mailbox's 'unavailable' greeting." },
  { flag: "b", label: "busy greeting", help: "Play the mailbox's 'busy' greeting." },
  { flag: "s", label: "skip instructions", help: "Skip the instructions and play only the beep." },
];

/**
 * Splits an Asterisk option string into the recognised single-letter flags and
 * whatever is left over (options that take arguments, e.g. "G(ctx^ext^1)").
 */
function splitFlags(value: string, known: string[]) {
  const active: string[] = [];
  let rest = "";
  for (let i = 0; i < value.length; i++) {
    const ch = value[i];
    if (known.includes(ch) && value[i + 1] !== "(") {
      if (!active.includes(ch)) active.push(ch);
      continue;
    }
    rest += ch;
    if (value[i + 1] === "(") {
      let depth = 0;
      let j = i + 1;
      for (; j < value.length; j++) {
        rest += value[j];
        if (value[j] === "(") depth++;
        else if (value[j] === ")") {
          depth--;
          if (depth === 0) break;
        }
      }
      i = j;
    }
  }
  return { active, rest };
}

function FlagPicker({ value, options, onChange, allowCustom = true, customPlaceholder }: {
  value: string;
  options: FlagOption[];
  onChange: (v: string) => void;
  allowCustom?: boolean;
  customPlaceholder?: string;
}) {
  const known = options.map((o) => o.flag);
  const { active, rest } = splitFlags(value ?? "", known);

  const emit = (nextActive: string[], nextRest: string) =>
    onChange(known.filter((k) => nextActive.includes(k)).join("") + nextRest);

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap gap-1">
        {options.map((o) => {
          const on = active.includes(o.flag);
          return (
            <button
              key={o.flag}
              type="button"
              title={o.help}
              aria-pressed={on}
              onClick={() => emit(on ? active.filter((a) => a !== o.flag) : [...active, o.flag], rest)}
              className={cn(
                "inline-flex cursor-pointer items-center gap-1 rounded-sm border px-1.5 py-0.5 text-[10px] transition-colors duration-150 ease-out",
                on
                  ? "border-transparent bg-primary text-primary-foreground"
                  : "border-input text-muted-foreground hover:border-border-hover hover:text-foreground"
              )}
            >
              <span className="font-mono font-semibold">{o.flag}</span>
              <span>{o.label}</span>
            </button>
          );
        })}
      </div>
      {allowCustom && (
        <Input
          value={rest}
          onChange={(e) => emit(active, e.target.value)}
          placeholder={customPlaceholder ?? "Other options, e.g. G(context^exten^1)"}
          className="h-7 text-xs"
        />
      )}
    </div>
  );
}

/** Mirrors the backend param models — keep in sync with schemas.py. */
function validateAction(action: ActionCreate): Record<string, string> {
  const p = action.params as Record<string, unknown>;
  const e: Record<string, string> = {};

  const required = (key: string, label: string) => {
    if (!String(p[key] ?? "").trim()) e[key] = `${label} is required.`;
  };
  const numeric = (key: string) => {
    const v = p[key];
    if (v === "" || v === undefined || v === null) return undefined;
    return Number(v);
  };
  const nonNegative = (key: string, label: string) => {
    const v = numeric(key);
    if (v !== undefined && (!Number.isFinite(v) || v < 0)) e[key] = `${label} cannot be negative.`;
  };

  switch (action.type) {
    case "answer":
      nonNegative("delay", "Delay");
      break;
    case "dial": {
      if ((p.target_type as string) === "websocket") required("connection_name", "WebSocket Connection");
      else required("endpoint_name", "Endpoint");
      nonNegative("timeout", "Timeout");
      break;
    }
    case "voicemail":
      required("mailbox", "Mailbox");
      break;
    case "set":
      required("variable", "Variable");
      break;
    case "goto": {
      required("context", "Context");
      required("extension", "Extension");
      const prio = numeric("priority");
      if (prio !== undefined && (!Number.isFinite(prio) || prio < 1)) e.priority = "Priority must be 1 or greater.";
      break;
    }
    case "stasis":
      required("app_name", "ARI App");
      break;
    case "playback":
      required("filename", "Filename");
      break;
    case "verbose":
      required("message", "Message");
      break;
    case "wait": {
      const seconds = numeric("seconds");
      if (seconds !== undefined && (!Number.isFinite(seconds) || seconds <= 0)) e.seconds = "Seconds must be greater than 0.";
      break;
    }
    case "queue":
      required("queue_name", "Queue Name");
      break;
    case "mixmonitor":
      required("filename", "Filename");
      break;
    case "bridge":
      required("channel", "Channel");
      break;
    case "record":
      required("filename", "Filename");
      nonNegative("silence", "Silence");
      nonNegative("maxduration", "Max Duration");
      break;
    case "sipaddheader":
      required("header", "Header");
      required("content", "Content");
      break;
    case "senddtmf":
      required("digits", "Digits");
      nonNegative("timeout_ms", "Timeout");
      nonNegative("duration_ms", "Duration");
      break;
    default:
      break;
  }
  return e;
}

/** Replaces empty numeric boxes with their defaults before submitting. */
function normalizeAction(action: ActionCreate, index: number): ActionCreate {
  const defaults = NUM_DEFAULTS[action.type] ?? {};
  const params = { ...(action.params as Record<string, unknown>) };
  for (const [key, fallback] of Object.entries(defaults)) {
    const v = params[key];
    params[key] = v === "" || v === undefined || v === null ? fallback : Number(v);
  }
  return { ...action, priority: index + 1, params };
}

function ActionForm({ action, onChange, onRemove, onMoveUp, onMoveDown, total, index, endpoints, wsEndpoints, ariApps, contexts, errors, showErrors }: {
  action: ActionCreate;
  onChange: (a: ActionCreate) => void;
  onRemove: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  total: number;
  index: number;
  endpoints: { name: string }[];
  wsEndpoints: { name: string }[];
  ariApps: { name: string }[];
  contexts: { name: string }[];
  errors: Record<string, string>;
  showErrors: boolean;
}) {
  const [expanded, setExpanded] = useState(true);
  const params = action.params as Record<string, unknown>;
  const hasErrors = showErrors && Object.keys(errors).length > 0;

  const updateType = (type: ActionType) => {
    let defaults: Record<string, unknown> = {};
    if (type === "dial") defaults = { target_type: "pjsip", endpoint_name: "", timeout: 30, flags: "" };
    if (type === "senddtmf") defaults = { digits: "", timeout_ms: 250, duration_ms: 0, channel: "", options: "" };
    onChange({ ...action, type, params: defaults });
  };

  const updateParam = (key: string, value: unknown) => {
    onChange({ ...action, params: { ...params, [key]: value } });
  };

  return (
    <Card className={cn("bg-secondary/30", hasErrors && "border-destructive-border")}>
      <CardContent className="p-3">
        <div className="flex items-center gap-2 mb-2">
          <div className="flex flex-col gap-0.5">
            {onMoveUp && <Button variant="ghost" size="icon" className="h-4 w-4" aria-label="Move action up" onClick={onMoveUp} disabled={index === 0}><ArrowUp size={10} /></Button>}
            {onMoveDown && <Button variant="ghost" size="icon" className="h-4 w-4" aria-label="Move action down" onClick={onMoveDown} disabled={index === total - 1}><ArrowDown size={10} /></Button>}
          </div>
          <Button variant="ghost" size="icon" className="h-6 w-6" aria-label={expanded ? "Collapse action" : "Expand action"} onClick={() => setExpanded(!expanded)}>
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </Button>
          <div className="flex-1 grid grid-cols-2 gap-2">
            <Field
              size="sm"
              label="Priority"
              info="Set automatically. Asterisk enters an extension at priority 1 and runs the actions in order, so priorities must start at 1 with no gaps. Use the arrows on the left to reorder."
            >
              <Input type="number" value={action.priority} readOnly disabled className="h-7 text-xs" />
            </Field>
            <Field
              size="sm"
              label="Action Type"
              required
              info="Required. The dialplan application this step executes. Changing it resets the parameters below."
            >
              <Select value={action.type} onChange={(e) => updateType(e.target.value as ActionType)} className="h-7 text-xs">
                {ACTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </Select>
            </Field>
          </div>
          <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" aria-label="Remove action" onClick={onRemove}>
            <Trash2 size={14} />
          </Button>
        </div>

        {expanded && (
          <ActionParams
            action={action}
            updateParam={updateParam}
            endpoints={endpoints}
            wsEndpoints={wsEndpoints}
            ariApps={ariApps}
            contexts={contexts}
            errors={errors}
            showErrors={showErrors}
          />
        )}
        {!expanded && hasErrors && (
          <p role="alert" className="text-[10px] text-destructive-text">
            This action has missing or invalid parameters — expand it to fix.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function ActionParams({ action, updateParam, endpoints, wsEndpoints, ariApps, contexts, errors, showErrors }: {
  action: ActionCreate;
  updateParam: (key: string, value: unknown) => void;
  endpoints: { name: string }[];
  wsEndpoints: { name: string }[];
  ariApps: { name: string }[];
  contexts: { name: string }[];
  errors: Record<string, string>;
  showErrors: boolean;
}) {
  const params = action.params as Record<string, unknown>;
  const err = (key: string) => (showErrors ? errors[key] : undefined);
  const numValue = (key: string, fallback: number) => {
    const v = params[key];
    return v === "" ? "" : ((v as number) ?? fallback);
  };
  const onNum = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    updateParam(key, e.target.value.trim() === "" ? "" : Number(e.target.value));

  switch (action.type) {
    case "answer":
      return (
        <div className="grid grid-cols-1 gap-2">
          <Field size="sm" label="Delay (ms)" error={err("delay")} info="Optional. Milliseconds to wait before answering the call. Empty or 0 answers immediately. A short delay gives caller ID lookups time to complete. Default 0.">
            <Input type="number" min={0} value={numValue("delay", 0)} error={!!err("delay")} onChange={onNum("delay")} placeholder="0" className="h-7 text-xs" />
          </Field>
        </div>
      );
    case "hangup":
      return (
        <Field size="sm" label="Cause" info="Optional. Q.850 cause code reported to the far end. Leave on Default to send 16 (normal clearing). Use 17 for busy or 21 for rejected so upstream systems can react correctly.">
          <Select value={(params.cause as string) ?? ""} onChange={(e) => updateParam("cause", e.target.value)} className="h-7 text-xs">
            {HANGUP_CAUSES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </Select>
        </Field>
      );
    case "dial":
      return (
        <div className="space-y-2">
          <Field size="sm" label="Target Type" required info="Required. PJSIP dials a configured SIP endpoint. WebSocket dials a WebSocket client connection, used for AI/STT media services.">
            <Select value={(params.target_type as string) ?? "pjsip"} onChange={(e) => updateParam("target_type", e.target.value)} className="h-7 text-xs">
              <option value="pjsip">PJSIP</option>
              <option value="websocket">WebSocket</option>
            </Select>
          </Field>
          {(params.target_type as string) === "websocket" ? (
            <>
              <Field size="sm" label="WebSocket Connection" required error={err("connection_name")} info="Required. The WebSocket client endpoint to dial. Defined under WebSocket Endpoints.">
                <Select value={(params.connection_name as string) ?? ""} error={!!err("connection_name")} onChange={(e) => updateParam("connection_name", e.target.value)} className="h-7 text-xs">
                  <option value="">Select...</option>
                  {wsEndpoints.map((w) => <option key={w.name} value={w.name}>{w.name}</option>)}
                </Select>
              </Field>
              <Field size="sm" label="Codec" info="Optional. Audio codec offered on the WebSocket media stream. Default ulaw.">
                <Select value={(params.codecs as string) ?? "ulaw"} onChange={(e) => updateParam("codecs", e.target.value)} className="h-7 text-xs">
                  {CODECS.map((c) => <option key={c} value={c}>{c}</option>)}
                </Select>
              </Field>
              <Field size="sm" label="Control Format" info="Optional. Encoding of the control messages sent over the WebSocket. Default json.">
                <Select value={(params.control_format as string) ?? "json"} onChange={(e) => updateParam("control_format", e.target.value)} className="h-7 text-xs">
                  <option value="json">JSON</option>
                  <option value="plain-text">Plain Text</option>
                </Select>
              </Field>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-[10px]">URI Parameters <HelpIcon text="Optional. Query parameters appended to the WebSocket dial string as URI-encoded key=value pairs. Useful for passing a tenant or call identifier to the media service." /></Label>
                  <Button type="button" variant="ghost" size="sm" className="h-5 text-[10px]" onClick={() => {
                    const existing = (params.query_params as Array<{ key: string; value: string }>) ?? [];
                    updateParam("query_params", [...existing, { key: "", value: "" }]);
                  }}>
                    <Plus size={10} /> Add Query Param
                  </Button>
                </div>
                {((params.query_params as Array<{ key: string; value: string }>) ?? []).map((qp, i) => (
                  <div key={i} className="flex items-center gap-1">
                    <Input value={qp.key} onChange={(e) => {
                      const qps = [...((params.query_params as Array<{ key: string; value: string }>) ?? [])];
                      qps[i] = { ...qps[i], key: e.target.value };
                      updateParam("query_params", qps);
                    }} placeholder="key" className="h-7 text-xs flex-1" />
                    <Input value={qp.value} onChange={(e) => {
                      const qps = [...((params.query_params as Array<{ key: string; value: string }>) ?? [])];
                      qps[i] = { ...qps[i], value: e.target.value };
                      updateParam("query_params", qps);
                    }} placeholder="value" className="h-7 text-xs flex-1" />
                    <Button type="button" variant="ghost" size="icon" className="h-6 w-6" aria-label="Remove query param" onClick={() => {
                      const qps = ((params.query_params as Array<{ key: string; value: string }>) ?? []).filter((_, idx) => idx !== i);
                      updateParam("query_params", qps);
                    }}>
                      <Trash2 size={12} />
                    </Button>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <Field size="sm" label="Endpoint" required error={err("endpoint_name")} info="Required. The PJSIP endpoint to ring. Must be an endpoint configured on this server.">
              <Select value={(params.endpoint_name as string) ?? ""} error={!!err("endpoint_name")} onChange={(e) => updateParam("endpoint_name", e.target.value)} className="h-7 text-xs">
                <option value="">Select...</option>
                {endpoints.map((ep) => <option key={ep.name} value={ep.name}>{ep.name}</option>)}
              </Select>
            </Field>
          )}
          <div className="grid grid-cols-1 gap-2">
            <Field size="sm" label="Timeout" error={err("timeout")} info="Optional. Maximum ring time in seconds before the dial attempt gives up and the dialplan continues. 0 rings indefinitely. Default 30.">
              <Input type="number" min={0} value={numValue("timeout", 30)} error={!!err("timeout")} onChange={onNum("timeout")} placeholder="30" className="h-7 text-xs" />
            </Field>
            <Field size="sm" label="Flags" info="Optional. Dial() option letters. Click to toggle the common ones; options that take arguments (G, L, S, M, U, A) go in the free-text box.">
              <FlagPicker value={(params.flags as string) ?? ""} options={DIAL_FLAGS} onChange={(v) => updateParam("flags", v)} />
            </Field>
          </div>
        </div>
      );
    case "voicemail":
      return (
        <div className="grid grid-cols-1 gap-2">
          <Field size="sm" label="Mailbox" required error={err("mailbox")} info="Required. Voicemail mailbox to leave the message in. Format: mailbox@context, as defined in voicemail.conf. Example: 1001@default.">
            <Input value={(params.mailbox as string) ?? ""} error={!!err("mailbox")} onChange={(e) => updateParam("mailbox", e.target.value)} placeholder="1001@default" className="h-7 text-xs" />
          </Field>
          <Field size="sm" label="Flags" info="Optional. Voicemail() option letters. u plays the unavailable greeting, b the busy greeting, s skips the greeting and instructions. Leave empty for the default unavailable greeting.">
            <FlagPicker value={(params.flags as string) ?? ""} options={VOICEMAIL_FLAGS} onChange={(v) => updateParam("flags", v)} customPlaceholder="Other options, e.g. g(3)" />
          </Field>
        </div>
      );
    case "set":
      return (
        <div className="grid grid-cols-2 gap-2">
          <Field size="sm" label="Variable" required error={err("variable")} info="Required. Channel variable or dialplan function to assign. Plain names such as MY_VAR persist for the call; functions such as CALLERID(name) change channel state.">
            <Input value={(params.variable as string) ?? ""} error={!!err("variable")} onChange={(e) => updateParam("variable", e.target.value)} placeholder="CALLERID(name)" className="h-7 text-xs" />
          </Field>
          <Field size="sm" label="Value" info="Optional. Value assigned to the variable; an empty value clears it. Other variables can be referenced with ${VARNAME}.">
            <Input value={(params.value as string) ?? ""} onChange={(e) => updateParam("value", e.target.value)} className="h-7 text-xs" />
          </Field>
        </div>
      );
    case "goto":
      return (
        <div className="space-y-2">
          <Field size="sm" label="Context" required error={err("context")} info="Required. Dialplan context to jump to. Must already exist, otherwise the call fails when the Goto executes.">
            <Select value={(params.context as string) ?? ""} error={!!err("context")} onChange={(e) => updateParam("context", e.target.value)} className="h-7 text-xs">
              <option value="">Select...</option>
              {contexts.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field size="sm" label="Extension" required error={err("extension")} info="Required. Extension to jump to inside that context. Example: 100, or s for the context's start extension.">
              <Input value={(params.extension as string) ?? ""} error={!!err("extension")} onChange={(e) => updateParam("extension", e.target.value)} placeholder="100" className="h-7 text-xs" />
            </Field>
            <Field size="sm" label="Priority" error={err("priority")} info="Optional. Priority to start at within the target extension. 1 runs it from the beginning. Default 1.">
              <Input type="number" min={1} value={numValue("priority", 1)} error={!!err("priority")} onChange={onNum("priority")} placeholder="1" className="h-7 text-xs" />
            </Field>
          </div>
        </div>
      );
    case "stasis":
      return (
        <div className="space-y-2">
          <Field size="sm" label="ARI App" required error={err("app_name")} info="Required. ARI application that takes control of the call. Must be registered under ARI Apps and connected, otherwise the call is hung up.">
            <Select value={(params.app_name as string) ?? ""} error={!!err("app_name")} onChange={(e) => updateParam("app_name", e.target.value)} className="h-7 text-xs">
              <option value="">Select...</option>
              {ariApps.map((a) => <option key={a.name} value={a.name}>{a.name}</option>)}
            </Select>
          </Field>
          <Field size="sm" label="Args" info="Optional. Comma-separated arguments delivered to the app in the StasisStart event. Variables such as ${EXTEN} can be used.">
            <Input value={(params.args as string) ?? ""} onChange={(e) => updateParam("args", e.target.value)} className="h-7 text-xs" />
          </Field>
        </div>
      );
    case "playback":
      return (
        <Field size="sm" label="Filename" required error={err("filename")} info="Required. Sound file to play, without the extension. Relative names resolve inside the Asterisk sounds directory. Example: hello-world.">
          <Input value={(params.filename as string) ?? ""} error={!!err("filename")} onChange={(e) => updateParam("filename", e.target.value)} placeholder="hello-world" className="h-7 text-xs" />
        </Field>
      );
    case "noop":
      return (
        <Field size="sm" label="Text" info="Optional. Text written to the Asterisk console when this step runs. NoOp does nothing else, so it is used for debugging and for evaluating variables.">
          <Input value={(params.text as string) ?? ""} onChange={(e) => updateParam("text", e.target.value)} className="h-7 text-xs" />
        </Field>
      );
    case "verbose":
      return (
        <div className="space-y-2">
          <Field size="sm" label="Level" info="Optional. Minimum console verbosity at which the message is printed. 0 is always shown; higher levels only appear when Asterisk runs at that verbosity or above. Default 0.">
            <Select value={String((params.level as number) ?? 0)} onChange={(e) => updateParam("level", Number(e.target.value))} className="h-7 text-xs">
              {VERBOSE_LEVELS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
            </Select>
          </Field>
          <Field size="sm" label="Message" required error={err("message")} info="Required. Text logged to the Asterisk console. Variables such as ${CALLERID(num)} are expanded.">
            <Input value={(params.message as string) ?? ""} error={!!err("message")} onChange={(e) => updateParam("message", e.target.value)} className="h-7 text-xs" />
          </Field>
        </div>
      );
    case "wait":
      return (
        <Field size="sm" label="Seconds" required error={err("seconds")} info="Required. How long to pause before the next action, in seconds. Decimals are allowed: 0.5 is half a second. Must be greater than 0.">
          <Input type="number" step="0.1" min={0} value={numValue("seconds", 1)} error={!!err("seconds")} onChange={onNum("seconds")} placeholder="1" className="h-7 text-xs" />
        </Field>
      );
    case "queue":
      return (
        <div className="grid grid-cols-1 gap-2">
          <Field size="sm" label="Queue Name" required error={err("queue_name")} info="Required. Name of a queue defined in queues.conf. Callers wait here until a member answers or the queue times out.">
            <Input value={(params.queue_name as string) ?? ""} error={!!err("queue_name")} onChange={(e) => updateParam("queue_name", e.target.value)} className="h-7 text-xs" />
          </Field>
          <Field size="sm" label="Options" info="Optional. Queue() option letters controlling transfer, hangup and hold behaviour while the caller waits. Options that take arguments, such as C or F(context^exten^pri), go in the free-text box.">
            <FlagPicker value={(params.options as string) ?? ""} options={QUEUE_OPTIONS} onChange={(v) => updateParam("options", v)} customPlaceholder="Other options, e.g. F(default^s^1)" />
          </Field>
        </div>
      );
    case "mixmonitor":
      return (
        <div className="grid grid-cols-1 gap-2">
          <Field size="sm" label="Filename" required error={err("filename")} info="Required. Target file for the mixed recording, including the format extension. Variables are expanded, so ${UNIQUEID} keeps each call separate. Example: /var/spool/asterisk/monitor/${UNIQUEID}.wav.">
            <Input value={(params.filename as string) ?? ""} error={!!err("filename")} onChange={(e) => updateParam("filename", e.target.value)} className="h-7 text-xs" />
          </Field>
          <Field size="sm" label="Options" info="Optional. MixMonitor() option letters. a appends instead of overwriting, b records only while the call is bridged. Options with arguments — v(x)/V(x)/W(x) for volume, r(file)/t(file) for split legs, m(mailbox), B(interval) — go in the free-text box.">
            <FlagPicker value={(params.options as string) ?? ""} options={MIXMONITOR_OPTIONS} onChange={(v) => updateParam("options", v)} customPlaceholder="Other options, e.g. V(2)" />
          </Field>
        </div>
      );
    case "return":
      return (
        <Field size="sm" label="Value" info="Optional. Value placed in the GOSUB_RETVAL channel variable for the caller of this subroutine. Only meaningful when this extension was entered with Gosub; a bare Return in a normal extension ends the call flow.">
          <Input value={(params.value as string) ?? ""} onChange={(e) => updateParam("value", e.target.value)} className="h-7 text-xs" />
        </Field>
      );
    case "bridge":
      return (
        <div className="grid grid-cols-1 gap-2">
          <Field size="sm" label="Channel" required error={err("channel")} info="Required. Name of an existing channel to bridge the current call to. Format: PJSIP/endpoint-name-00000001, usually taken from a channel variable rather than typed literally.">
            <Input value={(params.channel as string) ?? ""} error={!!err("channel")} onChange={(e) => updateParam("channel", e.target.value)} placeholder="PJSIP/phone-1001-00000001" className="h-7 text-xs" />
          </Field>
          <Field size="sm" label="Options" info="Optional. Bridge() option letters controlling courtesy tones, hangup, transfer and parking. Options with arguments — F(context^exten^pri), L(x), S(x) — go in the free-text box.">
            <FlagPicker value={(params.options as string) ?? ""} options={BRIDGE_OPTIONS} onChange={(v) => updateParam("options", v)} customPlaceholder="Other options, e.g. S(300)" />
          </Field>
        </div>
      );
    case "echo":
      return <p className="text-xs text-muted-foreground">No parameters. Echo() returns the caller's audio back to them until they press # — useful for testing audio paths.</p>;
    case "record":
      return (
        <div className="space-y-2">
          <Field size="sm" label="Filename" required error={err("filename")} info="Required. File to record into, including the format extension. Variables are expanded, so ${UNIQUEID} keeps each call separate. Example: /var/spool/asterisk/monitor/${UNIQUEID}.wav.">
            <Input value={(params.filename as string) ?? ""} error={!!err("filename")} onChange={(e) => updateParam("filename", e.target.value)} className="h-7 text-xs" />
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field size="sm" label="Silence" error={err("silence")} info="Optional. Seconds of silence that end the recording. 0 disables silence detection, so recording continues until a terminator key or hangup. Default 0.">
              <Input type="number" min={0} value={numValue("silence", 0)} error={!!err("silence")} onChange={onNum("silence")} placeholder="0" className="h-7 text-xs" />
            </Field>
            <Field size="sm" label="Max Duration" error={err("maxduration")} info="Optional. Hard limit on the recording length in seconds. 0 means no limit. Default 0.">
              <Input type="number" min={0} value={numValue("maxduration", 0)} error={!!err("maxduration")} onChange={onNum("maxduration")} placeholder="0" className="h-7 text-xs" />
            </Field>
          </div>
          <Field size="sm" label="Options" info="Optional. Record() option letters. q suppresses the beep, a appends to an existing file, x records until hangup, y stops on any DTMF digit.">
            <FlagPicker value={(params.options as string) ?? ""} options={RECORD_OPTIONS} onChange={(v) => updateParam("options", v)} customPlaceholder="Other options" />
          </Field>
        </div>
      );
    case "sipaddheader":
      return (
        <div className="grid grid-cols-2 gap-2">
          <Field size="sm" label="Header" required error={err("header")} info="Required. Header name added to the next outbound SIP request on this channel. Use an X- prefix for custom headers. Example: X-Custom-ID.">
            <Input value={(params.header as string) ?? ""} error={!!err("header")} onChange={(e) => updateParam("header", e.target.value)} placeholder="X-Custom-ID" className="h-7 text-xs" />
          </Field>
          <Field size="sm" label="Content" required error={err("content")} info="Required. Header value. Variables such as ${UNIQUEID} are expanded. Example: call-12345.">
            <Input value={(params.content as string) ?? ""} error={!!err("content")} onChange={(e) => updateParam("content", e.target.value)} placeholder="call-12345" className="h-7 text-xs" />
          </Field>
        </div>
      );
    case "senddtmf":
      return (
        <div className="space-y-2">
          <Field size="sm" label="Digits" required error={err("digits")} info="Required. Digits to send: 0-9, *, #, A-D. w pauses 0.5s, W pauses 1s, f/F sends a flash-hook. Example: w1234#.">
            <Input value={(params.digits as string) ?? ""} error={!!err("digits")} onChange={(e) => updateParam("digits", e.target.value)} placeholder="w1234#" className="h-7 text-xs" />
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field size="sm" label="Timeout (ms)" error={err("timeout_ms")} info="Optional. Gap between tones in milliseconds. Default 250.">
              <Input type="number" min={0} value={numValue("timeout_ms", 250)} error={!!err("timeout_ms")} onChange={onNum("timeout_ms")} placeholder="250" className="h-7 text-xs" />
            </Field>
            <Field size="sm" label="Duration (ms)" error={err("duration_ms")} info="Optional. Length of each tone in milliseconds. 0 uses the channel default. Default 0.">
              <Input type="number" min={0} value={numValue("duration_ms", 0)} error={!!err("duration_ms")} onChange={onNum("duration_ms")} placeholder="0" className="h-7 text-xs" />
            </Field>
          </div>
          <div className="grid grid-cols-1 gap-2">
            <Field size="sm" label="Channel" info="Optional. Channel to play the digits on. Leave empty to use the current channel; set it to send digits to the other leg of a bridged call.">
              <Input value={(params.channel as string) ?? ""} onChange={(e) => updateParam("channel", e.target.value)} className="h-7 text-xs" />
            </Field>
            <Field size="sm" label="Options" info="Optional. SendDTMF() has a single option: a answers the target channel first if it is not already up.">
              <FlagPicker value={(params.options as string) ?? ""} options={SENDDTMF_OPTIONS} onChange={(v) => updateParam("options", v)} allowCustom={false} />
            </Field>
          </div>
        </div>
      );
    default:
      return null;
  }
}

export default function ExtensionsPage() {
  const [searchParams] = useSearchParams();
  const ctxFromUrl = searchParams.get("context_id") ?? "";

  const { data: allExtensions, isLoading, error, refetch, isFetching } = useExtensions();
  const { data: contexts } = useContexts();
  const { data: endpoints } = useEndpoints();
  const { data: wsEndpoints } = useWebsocketEndpoints();
  const { data: ariApps } = useAriApps();
  const createMut = useCreateExtension();
  const updateMut = useUpdateExtension();
  const deleteMut = useDeleteExtension();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<DialplanExtension | null>(null);
  const [formCtxId, setFormCtxId] = useState("");
  const [formExtension, setFormExtension] = useState("");
  const [actions, setActions] = useState<ActionCreate[]>([]);
  const [showErrors, setShowErrors] = useState(false);
  const [query, setQuery] = useState("");
  const [pendingDelete, setPendingDelete] = useState<DialplanExtension | null>(null);

  const endpointNames = useMemo(() => endpoints?.map((e) => ({ name: e.name })) ?? [], [endpoints]);
  const wsNames = useMemo(() => wsEndpoints?.map((w) => ({ name: w.name })) ?? [], [wsEndpoints]);
  const ariNames = useMemo(() => ariApps?.map((a) => ({ name: a.name })) ?? [], [ariApps]);
  const contextNames = useMemo(() => contexts?.map((c) => ({ name: c.name })) ?? [], [contexts]);

  const contextNameById = useMemo(() => {
    const map = new Map<string, string>();
    contexts?.forEach((c) => map.set(c.id, c.name));
    return map;
  }, [contexts]);

  const columns: Column<DialplanExtension>[] = useMemo(
    () => [
      { key: "extension", header: "Extension" },
      {
        key: "context_id",
        header: "Context",
        render: (e) => {
          const name = contextNameById.get(e.context_id);
          return name ? <span>{name}</span> : <span className="text-muted-foreground">unknown context</span>;
        },
      },
      {
        key: "actions",
        header: "Actions",
        render: (e) => <div className="flex flex-wrap gap-1">{(e.actions || []).map((a) => <Badge key={a.id} variant="secondary" className="text-[10px]">{a.application} p{a.priority}</Badge>)}</div>,
      },
    ],
    [contextNameById]
  );

  const [filterCtx, setFilterCtx] = useState(ctxFromUrl);
  const list = useMemo(() => allExtensions?.filter((e) => !filterCtx || e.context_id === filterCtx) ?? [], [allExtensions, filterCtx]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((e) => e.extension.toLowerCase().includes(q));
  }, [list, query]);

  /** Priorities are positional: always 1..N in list order. */
  const applyActions = (next: ActionCreate[]) =>
    setActions(next.map((a, i) => ({ ...a, priority: i + 1 })));

  const openCreate = () => {
    setEditing(null);
    setFormCtxId(filterCtx || "");
    setFormExtension("");
    setActions([{ priority: 1, type: "answer", params: { delay: 0 } }]);
    setShowErrors(false);
    setOpen(true);
  };

  const openEdit = (ext: DialplanExtension) => {
    setEditing(ext);
    setFormCtxId(ext.context_id);
    setFormExtension(ext.extension);
    // Sort by stored priority, then renumber so any legacy gap (e.g. an
    // extension starting at priority 2, which Asterisk can never enter) is
    // repaired on the next save.
    setActions(
      [...(ext.actions || [])]
        .sort((a, b) => a.priority - b.priority)
        .map((a, i) => ({ priority: i + 1, type: a.application as ActionType, params: a.params }))
    );
    setShowErrors(true);
    setOpen(true);
  };

  const actionErrors = useMemo(() => actions.map((a) => validateAction(a)), [actions]);

  const validation = useMemo(() => {
    const fields: Record<string, string> = {};
    const extension = formExtension.trim();

    if (!formCtxId) fields.context_id = "Context is required.";
    if (!extension) fields.extension = "Extension is required.";
    else if (ILLEGAL_EXTENSION_CHARS.test(extension)) fields.extension = "Extension cannot contain spaces, commas, semicolons or line breaks.";
    else if (extension.length > EXTENSION_MAX_CHARS) fields.extension = `Extension must be ${EXTENSION_MAX_CHARS} characters or fewer.`;
    else if (
      formCtxId &&
      (allExtensions ?? []).some((e) => e.context_id === formCtxId && e.extension === extension && e.id !== editing?.id)
    ) fields.extension = "This context already has an extension with that name.";

    if (actions.length === 0) fields.actions = "Add at least one action — an extension with no actions is never generated.";

    const summary = Object.values(fields);
    actionErrors.forEach((errs, i) => {
      for (const msg of Object.values(errs)) {
        summary.push(`Action ${i + 1} (${ACTION_LABELS[actions[i].type] ?? actions[i].type}): ${msg}`);
      }
    });

    return { fields, summary, valid: summary.length === 0 };
  }, [formCtxId, formExtension, actions, actionErrors, allExtensions, editing]);

  const handleSave = () => {
    setShowErrors(true);
    if (!validation.valid) return;

    const data = {
      context_id: formCtxId,
      extension: formExtension.trim(),
      actions: actions.map(normalizeAction),
    };
    if (editing) {
      updateMut.mutate({ id: editing.id, data }, { onSuccess: () => { setOpen(false); toast.success("Extension updated"); }, onError: (e) => toast.error(`Failed: ${(e as Error).message}`) });
    } else {
      createMut.mutate(data, { onSuccess: () => { setOpen(false); toast.success("Extension created"); }, onError: (e) => toast.error(`Failed: ${(e as Error).message}`) });
    }
  };

  const addAction = () => {
    setShowErrors(true);
    applyActions([...actions, { priority: actions.length + 1, type: "noop", params: { text: "" } }]);
  };

  const updateAction = (idx: number, action: ActionCreate) => {
    setShowErrors(true);
    const next = [...actions];
    next[idx] = action;
    applyActions(next);
  };

  const removeAction = (idx: number) => {
    // Renumbering matters: a gap at the front leaves an extension starting at
    // priority 2, which Asterisk can never enter.
    applyActions(actions.filter((_, i) => i !== idx));
  };

  const moveActionUp = (idx: number) => {
    if (idx === 0) return;
    const next = [...actions];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    applyActions(next);
  };

  const moveActionDown = (idx: number) => {
    if (idx === actions.length - 1) return;
    const next = [...actions];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    applyActions(next);
  };

  const err = (key: string) => (showErrors ? validation.fields[key] : undefined);

  return (
    <div className="space-y-4">
      <PageHeader title="Extensions" description="Dialplan extensions with typed actions" />

      <Toolbar
        search={{ value: query, onChange: setQuery, placeholder: "Search extensions..." }}
        filters={
          <div className="flex items-center gap-3">
            <Label className="text-sm">Filter by context:</Label>
            <Select value={filterCtx} onChange={(e) => setFilterCtx(e.target.value)} className="w-64">
              <option value="">All contexts</option>
              {contexts?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </div>
        }
        count={`${filtered.length}${query ? ` of ${list.length}` : ""}`}
        onRefresh={() => refetch()}
        refreshing={isFetching}
        actions={<Button onClick={openCreate} size="sm"><Plus size={14} /> Create Extension</Button>}
      />

      <DataTable
        columns={columns}
        data={filtered}
        isLoading={isLoading}
        getRowKey={(e) => e.id}
        onEdit={openEdit}
        onDelete={setPendingDelete}
        emptyMessage={query ? "No matches for your search." : "No extensions configured."}
        error={error}
        onRetry={() => refetch()}
      />

      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? "Edit Extension" : "Create Extension"}
        description="Dialplan extension with an ordered list of actions."
        size="lg"
        footer={
          <>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSave}
              disabled={!validation.valid}
              loading={createMut.isPending || updateMut.isPending}
            >
              {editing ? "Update" : "Create"}
            </Button>
          </>
        }
      >
        <div className="space-y-5">
          <FormSection title="Extension" columns={2}>
            <Field
              label="Context"
              required
              htmlFor="context_id"
              error={err("context_id")}
              info="Required. The dialplan context this extension is written into. Calls only reach it if they arrive in this context, so it must match the context set on the endpoint that receives them. Changing it on an existing extension moves the extension to the other context."
            >
              <Select id="context_id" value={formCtxId} error={!!err("context_id")} onChange={(e) => { setShowErrors(true); setFormCtxId(e.target.value); }}>
                <option value="">Select context</option>
                {contexts?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </Field>
            <Field
              label="Extension"
              required
              htmlFor="extension"
              error={err("extension")}
              info="Required. The dialled number or pattern this entry matches. Exact: 100. Patterns start with an underscore: _1XX matches 100-199, _X. matches any number of digits, and character ranges such as _9[0-8]X are allowed. s is the start extension used when no number is dialled. Must be unique within the context and cannot contain spaces, commas or semicolons."
            >
              <Input id="extension" value={formExtension} error={!!err("extension")} maxLength={EXTENSION_MAX_CHARS} onChange={(e) => { setShowErrors(true); setFormExtension(e.target.value); }} placeholder="100" />
            </Field>
          </FormSection>

          <Field
            label="Actions"
            required
            error={err("actions")}
            className="space-y-2"
            info="Required. The ordered steps executed when the extension is matched. At least one action is needed. Asterisk always enters an extension at priority 1 and runs the steps in order, so priorities are assigned automatically — reorder with the arrows."
          >
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={addAction}><Plus size={12} /> Add Action</Button>
            </div>
            <div className="space-y-2">
              {actions.map((action, idx) => (
                <ActionForm
                  key={idx}
                  action={action}
                  onChange={(a) => updateAction(idx, a)}
                  onRemove={() => removeAction(idx)}
                  onMoveUp={() => moveActionUp(idx)}
                  onMoveDown={() => moveActionDown(idx)}
                  total={actions.length}
                  index={idx}
                  endpoints={endpointNames}
                  wsEndpoints={wsNames}
                  ariApps={ariNames}
                  contexts={contextNames}
                  errors={actionErrors[idx] ?? {}}
                  showErrors={showErrors}
                />
              ))}
              {actions.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No actions defined. Click "Add Action" to start.</p>}
            </div>
            <FieldNote>
              Steps run top to bottom. Answer the call before playing audio, and end with Hangup unless another action transfers the call.
            </FieldNote>
          </Field>

          <ValidationSummary errors={validation.summary} />
        </div>
      </Sheet>

      <ConfirmDialog
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        title="Delete extension"
        description={<>Delete extension <strong>{pendingDelete?.extension}</strong>? This cannot be undone.</>}
        loading={deleteMut.isPending}
        onConfirm={() =>
          pendingDelete &&
          deleteMut.mutate(pendingDelete.id, {
            onSuccess: () => { toast.success("Extension deleted"); setPendingDelete(null); },
            onError: (e) => toast.error(`Failed: ${(e as Error).message}`),
          })
        }
      />
    </div>
  );
}
